import { useEffect, useMemo, useState } from 'react';
import { useApplicationIdContext } from '../ApplicationContext';
import { DocumentRequirement } from './SingleUploadBox';
import useTrigger from '@n2-common/lib/useTrigger';
import { getApplicationDocumentStatus, removeDocument, startSbl, uploadDocument } from '@n2-common/services/legacyApplicationApiService';
import { DocumentationContext, DocumentationContextType } from './Context';
import { SigningFileUploadSection } from '@n2-common/serverGeneratedTypings/signingFileUploadSection';
import useRefresh, { Condition } from '@n2-common/lib/useRefresh';
import { logErrorToServerside, logInfoToServerSide } from '@n2-common/services/serverSideLogging';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { GenerateSblUrlResponse } from '@n2-common/serverGeneratedTypings/generateSblUrlResponse';

export const DocumentationContextProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
    const { t } = useTranslation('common');
    const { applicationId, applicantId, country } = useApplicationIdContext();
    const [requirements, setRequirements] = useState<DocumentRequirement[]>([]);
    const { key, touch } = useTrigger();
    const { refreshUntil } = useRefresh(touch, requirements);

    useEffect(() => {
        if (!applicantId || !applicationId) {
            return;
        }
        const controller = new AbortController();
        getApplicationDocumentStatus(applicationId, applicantId, controller.signal).then((res) => {
            setRequirements(res.Documents.map(documentRequirementMapper(country)));
        });
        return () => controller.abort();
    }, [applicantId, applicationId, country, key]);

    const context = useMemo(
        () => createPartialContext(country, applicationId, applicantId, refreshUntil, t),
        [applicantId, applicationId, country, refreshUntil, t]
    );

    return <DocumentationContext.Provider value={{ requirements, country, ...context }}>{children}</DocumentationContext.Provider>;
};

export function createPartialContext(
    country: Country,
    applicationId: string,
    applicantId: string,
    refreshUntil: (refreshCondition: Condition<DocumentRequirement[]>) => Promise<void>,
    t: TFunction
): Pick<DocumentationContextType, 'trustedUpload' | 'deleteDocument' | 'createUploadDocumentHandler' | 'completeUpload'> {
    return {
        trustedUpload:
            country == 'NO'
                ? async function trustedUploadHandler() {
                      let res: GenerateSblUrlResponse | null = null;
                      try {
                          res = await startSbl(applicationId);
                      } catch (err) {
                          logErrorToServerside('Redirect to trusted upload failed', applicationId, applicantId, err);
                          throw t<string>('common:Sign.Documents.Trusted.RedirectError');
                      }
                      if (res.Status === 'DataNotFound') {
                          throw t<string>('common:Sign.Documents.Trusted.DataNotFoundError');
                      }
                      if (res.Status !== 'Success') {
                          throw t<string>('common:Sign.Documents.Trusted.RedirectError');
                      }
                      if (!res.Uri) {
                          const message = 'startSbl did not return a redirect url.';
                          logErrorToServerside(message, applicationId, applicantId, null);
                          throw new Error(message);
                      }
                      window.location.href = res.Uri;
                  }
                : async () => {
                      await logInfoToServerSide(
                          `Trusted upload is being called for '${country}', but it is not implemented.`,
                          applicationId,
                          applicantId
                      );
                  },

        deleteDocument: async function deleteDocumentHandler(documentId: string) {
            try {
                await removeDocument(applicationId, applicantId, documentId);
                const condition = (requirements: DocumentRequirement[]) =>
                    !requirements.some((r) => r.uploadedDocuments.find((d) => d.id == documentId));
                await refreshUntil(condition);
            } catch (err) {
                logErrorToServerside('Delete documentation failed', applicationId, applicantId, err);
                throw t<string>('Sign.Documents.DeleteError');
            }
        },

        createUploadDocumentHandler: (documentType: string) =>
            async function uploadDocumentHandler(data: FormData) {
                try {
                    const res = await uploadDocument(applicationId, applicantId, documentType, data);
                    const condition = (requirements: DocumentRequirement[]) => {
                        const docs = requirements
                            .find((r) => r.documentType == documentType)
                            ?.uploadedDocuments.filter((d) => res.includes(d.ref))
                            .flat();
                        return docs?.length == res.length;
                    };
                    await refreshUntil(condition);
                } catch (err) {
                    logErrorToServerside('Upload documentation failed', applicationId, applicantId, err);
                    throw t<string>('Sign.Documents.UploadError');
                }
            },

        completeUpload: async function (): Promise<void> {
            // NOOP in O1
        },
    };
}

export function documentRequirementMapper(country: Country) {
    return (section: SigningFileUploadSection): DocumentRequirement => ({
        documentType: section.ExtendedDocumentType,
        canUpload: section.AllowUpload === null ? true : section.AllowUpload,
        canUseTrusted: country == 'NO' && !!section.IsSblSection && !section.UploadedDocuments.some((d) => d.IsTrusted),
        completed: section.Completed || false,
        help: {
            heading: section.HelpTextHeading,
            description: section.HelpTextBody,
            imageUrl: section.UploadExampleImageUrl && `/Content/documentExamples/${country.toLocaleLowerCase()}/${section.UploadExampleImageUrl}`,
        },
        uploadedDocuments: section.UploadedDocuments.map((d) => ({
            id: d.DocumentRef,
            filename: d.Filename,
            error: d.ErrorText || undefined,
            status: d.DocumentStatus,
            trusted: d.IsTrusted,
            ref: d.DocumentGuid,
            deleteable: (d.DocumentStatus == 'P.New' || !!d.ErrorText) && !d.IsTrusted,
        })),
    });
}
